/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXValue } from '@cloudio-saas/statex';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import useWindowSize from 'hooks/useWindowSize';
import Icon from 'icons/Icon';
import AboutPage from 'layout/Header/AboutPage';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useEffect, useState } from 'react';
import ReactTimeago from 'react-timeago';
import useUser from 'root/useUser';
import { getURLQueryStringToPost } from 'utils/url';
import { getUrl } from '../../config/config';
import logo from './logo.png';
import { useOrgNameAndLogo } from './OrgLogo';

export const PLATFORM_VERSION = '4.1.0';
export const BUILD_VERSION = '1012';
export const BUILD_DATE = '2026-04-10T20:43:34.560Z';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    cursor: 'pointer',
    marginLeft: theme.spacing(1),
  },
  label: {
    textAlign: 'end',
    paddingRight: theme.spacing(1),
    minWidth: 160,
  },
}));

export default function About({
  onClose,
}: {
  onClose: () => void;
}): React.ReactElement | null {
  const classes = useStyles();
  const [serverState, setServerState] = useState({
    version: '',
    date: '',
    trial_version_message: '',
  });
  const user = useUser();
  const [{ orgName }] = useOrgNameAndLogo();
  const [ww, wh] = useWindowSize();
  const platform = usePlatform();
  const page = platform.getPage(platform.appUid, platform.pageId);
  const pageName = useStateXValue(
    ['db', 'IoPages', platform.pageId, 'pageName'],
    '',
  );
  const [showAboutPage, setShowAboutPage] = useState(false);

  useEffect(() => {
    fetch(`${getUrl()}about?${getURLQueryStringToPost(user)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((r) => {
        setServerState(r);
      });
  }, [user, user.x]);

  return (
    <Dialog onClose={onClose} fullScreen={ww <= 600} open>
      <DialogTitle sx={{ m: 0, p: 2, pr: '72px' }}>
        About {orgName}
        <IconButton
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
          }}
          onClick={onClose}
        >
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4, pt: 0 }}>
        <table>
          <tbody>
            <tr>
              <td className={classes.label}>Platform Version</td>
              <td>{PLATFORM_VERSION}</td>
            </tr>
            <tr>
              <td className={classes.label}>Build Version</td>
              <td>
                {BUILD_VERSION} - <ReactTimeago date={BUILD_DATE} />
              </td>
            </tr>
            {serverState.date ? (
              <tr>
                <td className={classes.label}>Server Build</td>
                <td>
                  {serverState.version} -{' '}
                  <ReactTimeago date={serverState.date} />
                </td>
              </tr>
            ) : null}
            {serverState.trial_version_message ? (
              <tr>
                <td className={classes.label}>Trial Version</td>
                <td>{serverState.trial_version_message}</td>
              </tr>
            ) : null}
            <tr>
              <td className={classes.label}>React Version</td>
              <td>{React.version}</td>
            </tr>
            <tr>
              <td className={classes.label}>User Agent</td>
              <td>{navigator.userAgent}</td>
            </tr>
            <tr>
              <td className={classes.label}>Viewport Size</td>
              <td>
                {ww} x {wh}
              </td>
            </tr>
            {page ? (
              <tr>
                <td className={classes.label}>Page</td>
                <td>
                  <Button
                    onClick={() => {
                      setShowAboutPage(true);
                    }}
                  >
                    {pageName} - {Object.keys(page).length} components
                  </Button>
                  {showAboutPage && (
                    <AboutPage onClose={() => setShowAboutPage(false)} />
                  )}
                </td>
              </tr>
            ) : null}
            <tr>
              <td className={classes.label}>Powered by</td>
              <td>
                <img
                  alt="Logo"
                  style={{ verticalAlign: 'middle' }}
                  height={40}
                  src={logo}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}
